import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  LinearProgress,
  TableCell,
  TableRow,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { AssessmentStatus } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { Assessment } from '@libs/types';
import {
  ChartBarIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';

import { Badge } from '../../../../components/Badge';
import { CompactRunBar } from '../RunProgress/CompactRunBar';

import { AssessmentActions } from './AssessmentActions';
import { ScorePill } from './ScorePill';
import { useAssessmentUtils } from './useAssessmentUtils';

interface AssessmentVersionRowProps {
  version: Assessment;
  isLast: boolean;
}

const statusBarColor = (status: AssessmentStatus, theme: Theme) => {
  switch (status) {
    case AssessmentStatus.COMPLETED:
      return theme.palette.accent.green;
    case AssessmentStatus.PROCESSING:
      return theme.palette.accent.blue;
    case AssessmentStatus.FAILED:
      return theme.palette.error.main;
    case AssessmentStatus.SUBMITTED:
      return theme.palette.warning.main;
    case AssessmentStatus.DRAFT:
    default:
      return theme.palette.black[400];
  }
};

const tertiaryButtonSx = (theme: Theme) => ({
  textTransform: 'none' as const,
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 1.5,
  px: 1.25,
  py: 0.375,
  minWidth: 'auto',
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.grey[300]}`,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
    boxShadow: 'none',
  },
});

export const AssessmentVersionRow: FC<AssessmentVersionRowProps> = observer(
  ({ version, isLast }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { getStatusTranslationKey, getAssessmentStatusVariant, formatUpdatedTime, formatEvaluationDuration } =
      useAssessmentUtils();

    const isCompleted = version.status === AssessmentStatus.COMPLETED;
    const isDraft = version.status === AssessmentStatus.DRAFT;
    const barColor = statusBarColor(version.status, theme);

    const handleViewResults = () => navigate(routes.assessmentOverview(version.id));
    const handleEditDraft = () =>
      navigate(routes.assessmentDomain(version.id, version.currentDomainIndex));

    return (
      <TableRow
        sx={{
          backgroundColor: theme.palette.blue[100],
          '& > td': {
            borderBottom: isLast
              ? `2px solid ${theme.palette.grey[300]}`
              : `1px solid ${theme.palette.blue[200]}`,
            backgroundColor: 'inherit',
          },
          '& > td:first-of-type': {
            borderInlineStart: `3px solid ${theme.palette.accent.blue}`,
          },
          '&:hover': {
            backgroundColor: theme.palette.blue[200],
          },
          transition: 'background-color .1s',
        }}
      >
        {/* Title cell — indented */}
        <TableCell sx={{ py: 1, pl: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: barColor,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: 13, fontWeight: 500, color: theme.palette.text.primary }}
            >
              <Trans
                i18nKey="app.assessments.versionHistory.versionLabel"
                values={{ n: version.version }}
              />
            </Typography>
            {version.language?.label && (
              <Badge
                label={version.language.label}
                variant="neutral"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Box>
        </TableCell>

        {/* Status */}
        <TableCell sx={{ py: 1 }}>
          <Badge
            label={<Trans i18nKey={getStatusTranslationKey(version.status)} />}
            variant={getAssessmentStatusVariant(version.status)}
          />
        </TableCell>

        {/* Progress */}
        <TableCell sx={{ py: 1 }}>
          {/* Same rule as the parent row: a live run gets the counted bar. */}
          {version.runProgress ? (
            <CompactRunBar progress={version.runProgress} status={version.status} />
          ) : (version.status === AssessmentStatus.DRAFT ||
            version.status === AssessmentStatus.PROCESSING ||
            version.status === AssessmentStatus.COMPLETED ||
            version.status === AssessmentStatus.FAILED) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={version.progress}
                sx={{
                  flex: 1,
                  minWidth: 72,
                  height: 8,
                  borderRadius: 9999,
                  backgroundColor: theme.palette.grey[300],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: barColor,
                    borderRadius: 9999,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  minWidth: 32,
                  fontWeight: 500,
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                }}
              >
                {version.progress}%
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.tertiary, fontSize: 13 }}
            >
              —
            </Typography>
          )}
        </TableCell>

        {/* Score */}
        <TableCell sx={{ py: 1 }}>
          <ScorePill score={isCompleted ? version.score : null} size="sm" />
        </TableCell>

        {/* Submitted At */}
        <TableCell sx={{ py: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: 12, color: theme.palette.text.tertiary }}
          >
            {version.submittedAt ? formatUpdatedTime(version.submittedAt) : '—'}
          </Typography>
        </TableCell>

        {/* Evaluation duration (submitted → completed) */}
        <TableCell sx={{ py: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: 12, color: theme.palette.text.tertiary }}
          >
            {formatEvaluationDuration(version.submittedAt, version.completedAt)}
          </Typography>
        </TableCell>

        {/* Created At */}
        <TableCell sx={{ py: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: 12, color: theme.palette.text.tertiary }}
          >
            {formatUpdatedTime(version.createdAt)}
          </Typography>
        </TableCell>

        {/* Last Updated */}
        <TableCell sx={{ py: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: 12, color: theme.palette.text.tertiary }}
          >
            {formatUpdatedTime(version.updatedAt)}
          </Typography>
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ py: 1, pr: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            {isCompleted && (
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<ChartBarIcon size={13} />}
                onClick={handleViewResults}
                sx={tertiaryButtonSx(theme)}
              >
                <Trans i18nKey="app.assessments.actions.viewResults" />
              </Button>
            )}
            {isDraft && (
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<PencilSimpleIcon size={13} />}
                onClick={handleEditDraft}
                sx={tertiaryButtonSx(theme)}
              >
                <Trans i18nKey="app.assessments.actions.resume" />
              </Button>
            )}
            <AssessmentActions assessment={version} hideDelete hideDuplicate />
          </Box>
        </TableCell>
      </TableRow>
    );
  },
);
